import { useCallback } from "react";
import { match } from "ts-pattern";
import { checkIsTimestampedTopicTag, useAllTags } from "~/domain/logs";
import type { Topic } from "~/lqs";
import { usePlayerParams } from "../../hooks";
import type {
  ChartPanel,
  ChartTopicDescriptor,
  PairedTopic,
  PairedTopics,
} from "../../panels";
import type { PlayerRecord, RecordType } from "../../record-store";
import type { TimestepValue } from "../../types";
import { floorTimestampForTimestep } from "../../utils";

export type TagTimestamps = Partial<Record<Topic["id"], ReadonlyArray<bigint>>>;

export function useTagTimestamps(
  pairedTopics:
    | PairedTopics<ChartPanel["topics"]>
    | PairedTopic<ChartTopicDescriptor>,
) {
  const { logId } = usePlayerParams();

  const query = useAllTags(logId, {
    select: useCallback(
      (response): TagTimestamps => {
        const tagTimestamps: TagTimestamps = {};

        const topicIds = Array.isArray(pairedTopics)
          ? pairedTopics.map(({ topic: { id } }) => id)
          : [(pairedTopics as PairedTopic<ChartTopicDescriptor>).topic.id];

        for (const tag of response.data) {
          if (!checkIsTimestampedTopicTag(tag)) {
            continue;
          }

          if (!topicIds.includes(tag.topicId)) {
            continue;
          }

          if (!(tag.topicId in tagTimestamps)) {
            tagTimestamps[tag.topicId] = [];
          }

          (tagTimestamps[tag.topicId] as Array<bigint>).push(tag.startTime);

          if (tag.endTime != null) {
            (tagTimestamps[tag.topicId] as Array<bigint>).push(tag.endTime);
          }
        }

        return tagTimestamps;
      },
      [pairedTopics],
    ),
  });

  return match(query)
    .with({ status: "loading" }, () => null)
    .with({ status: "error" }, () => ({}))
    .with({ status: "success" }, ({ data }) => data)
    .exhaustive();
}

export function replaceWithTaggedRecord<TRecordType extends RecordType>(
  base: PlayerRecord<TRecordType>,
  tagRecords: ReadonlyArray<PlayerRecord<TRecordType>>,
  timestep: TimestepValue,
): PlayerRecord<TRecordType> {
  const flooredBaseTimestamp = floorTimestampForTimestep(
    base.timestamp,
    timestep,
  );

  return (
    tagRecords.find(
      (tagRecord) =>
        floorTimestampForTimestep(tagRecord.timestamp, timestep) ===
        flooredBaseTimestamp,
    ) ?? base
  );
}
